import type {
  UmmStatus,
  GasFacilityUnavailabilityType,
  Direction,
  UmmType,
  UnavailabilityType,
  GasFacilityUnitOfMeasurementType,
  MmStatus,
  TypeOfMaintenance,
  UmmsCombinedSortBy,
  MmCombinedSortBy,
  MmSortBy,
  UmmsSortBy,
  UmmsSortDirection,
  MmSortDirection,
} from 'src/apis/iip-service/types';
import { labelsToOptions } from 'src/utils/labels-to-options';
import { labelsToValues } from 'src/utils/labels-to-values';

export const gasFacilityUnavailabilityTypeLabels: Record<
  GasFacilityUnavailabilityType,
  string
> = {
  OFFSHORE_PIPELINE_UNAVAILABILITY: 'Offshore Pipeline Unavailability',
  TRANSMISSION_SYSTEM_UNAVAILABILITY: 'Transmission System Unavailability',
  STORAGE_UNAVAILABILITY: 'Storage Unavailability',
  STORAGE_FACILITY_UNAVAILABILITY: 'Storage Facility Unavailability',
  INJECTION_UNAVAILABILITY: 'Injection Unavailability',
  WITHDRAWAL_UNAVAILABILITY: 'Withdrawal Unavailability',
  GAS_TREATMENT_PLANT_UNAVAILABILITY: 'Gas Treatment Plant Unavailability',
  REGASIFICATION_PLANT_UNAVAILABILITY: 'Regasification Plant Unavailability',
  COMPRESSOR_STATION_UNAVAILABILITY: 'Compressor Station Unavailability',
  GAS_PRODUCTION_FIELD_UNAVAILABILITY: 'Gas Production Field Unavailability',
  IMPORT_CONTRACT_CURTAILMENT: 'Import Contract Curtailment',
  CONSUMPTION_UNAVAILABILITY: 'Consumption Unavailability',
  OTHER_UNAVAILABILITY: 'Other Unavailability',
};

export const gasFacilityUnavailabilityTypeOptions = labelsToOptions(
  gasFacilityUnavailabilityTypeLabels
);
export const gasFacilityUnavailabilityTypeValues = labelsToValues(
  gasFacilityUnavailabilityTypeLabels
);

export const ummTypeLabels: Record<UmmType, string> = {
  UNAVAILABILITY_OF_GAS_FACILITIES: 'Unavailability of Gas Facilities',
  OTHER_MARKET_INFORMATION: 'Other Market Information',
};

export const ummTypeOptions = labelsToOptions(ummTypeLabels);
export const ummTypeValues = labelsToValues(ummTypeLabels);

export const gasFacilityUnitOfMeasurementTypeLabels: Record<
  GasFacilityUnitOfMeasurementType,
  string
> = {
  KWH_PER_DAY: 'kWh/d',
  KWH_PER_HOUR: 'kWh/h',
  GWH: 'GWh',
  GWH_PER_DAY: 'GWh/d',
  TWH: 'TWh',
  MCM_PER_DAY: 'mcm/d', // only v1
  GWH_PER_HOUR: 'GWh/h', // only v2
};

const {
  MCM_PER_DAY,
  GWH_PER_HOUR,
  ...baseGasFacilityUnitOfMeasurementTypeLabels
} = gasFacilityUnitOfMeasurementTypeLabels;

export const gasFacilityUnitOfMeasurementTypeOptionsV1 = labelsToOptions({
  ...baseGasFacilityUnitOfMeasurementTypeLabels,
  MCM_PER_DAY,
});

export const gasFacilityUnitOfMeasurementTypeOptionsV2 = labelsToOptions({
  ...baseGasFacilityUnitOfMeasurementTypeLabels,
  GWH_PER_HOUR,
});

export const unitOfMeasurementValues = labelsToValues(
  gasFacilityUnitOfMeasurementTypeLabels
);

export const directionLabels: Record<Direction, string> = {
  ENTRY: 'Entry',
  EXIT: 'Exit',
};

export const directionOptions = labelsToOptions(directionLabels);
export const directionValues = labelsToValues(directionLabels);

export const unavailabilityTypeLabels: Record<UnavailabilityType, string> = {
  PLANNED: 'Planned',
  UNPLANNED: 'Unplanned',
};

export const unavailabilityTypeOptions = labelsToOptions(
  unavailabilityTypeLabels
);
export const unavailabilityTypeValues = labelsToValues(
  unavailabilityTypeLabels
);

export const mmCombinedSortByLabels: Record<MmCombinedSortBy, string> = {
  PUBLICATION_DATE_TIME_DESC: 'Publication Date (Newest First)',
  PUBLICATION_DATE_TIME_ASC: 'Publication Date (Oldest First)',
  EVENT_PERIOD_START_DESC: 'Event Period Start (Newest First)',
  EVENT_PERIOD_START_ASC: 'Event Period Start (Oldest First)',
};

export const mmCombinedSortByOptions = labelsToOptions(mmCombinedSortByLabels);
export const mmCombinedSortByValues = labelsToValues(mmCombinedSortByLabels);

export const mapMmCombinedSortByToSortBy = (
  combinedSortBy: MmCombinedSortBy
): { sortBy: MmSortBy; sortDirection: MmSortDirection } => {
  const lastUnderscoreIndex = combinedSortBy.lastIndexOf('_');
  const sortBy = combinedSortBy.substring(0, lastUnderscoreIndex) as MmSortBy;
  const sortDirection = combinedSortBy.substring(
    lastUnderscoreIndex + 1
  ) as MmSortDirection;
  return { sortBy, sortDirection };
};

export const mmStatusLabels: Record<MmStatus, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  DISMISSED: 'Dismissed',
};

export const mmStatusValues = labelsToValues(mmStatusLabels);
export const mmStatusOptions = labelsToOptions(mmStatusLabels);

export const typeOfMaintenanceLabels: Record<TypeOfMaintenance, string> = {
  PLANNED: 'Planned',
  UNPLANNED: 'Unplanned',
};

export const typeOfMaintenanceOptions = labelsToOptions(
  typeOfMaintenanceLabels
);

export const typeOfMaintenanceValues = labelsToValues(typeOfMaintenanceLabels);

export const ummStatusLabels: Record<UmmStatus, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  DISMISSED: 'Dismissed',
};

export const ummStatusValues = labelsToValues(ummStatusLabels);
export const ummStatusOptions = labelsToOptions(ummStatusLabels);

export const ummCombinedSortByLabels: Record<UmmsCombinedSortBy, string> = {
  PUBLICATION_DATE_TIME_DESC: 'Publication Date (Newest First)',
  PUBLICATION_DATE_TIME_ASC: 'Publication Date (Oldest First)',
  EVENT_START_DESC: 'Event Period Start (Newest First)',
  EVENT_START_ASC: 'Event Period Start (Oldest First)',
};

export const ummCombinedSortByValues = labelsToValues(ummCombinedSortByLabels);
export const ummCombinedSortByOptions = labelsToOptions(
  ummCombinedSortByLabels
);

export const mapUmmsCombinedSortByToSortBy = (
  combinedSortBy: UmmsCombinedSortBy
): { sortBy: UmmsSortBy; sortDirection: UmmsSortDirection } => {
  const lastUnderscoreIndex = combinedSortBy.lastIndexOf('_');
  const sortBy = combinedSortBy.substring(0, lastUnderscoreIndex) as UmmsSortBy;
  const sortDirection = combinedSortBy
    .substring(lastUnderscoreIndex + 1)
    .toUpperCase() as UmmsSortDirection;
  return { sortBy, sortDirection };
};
